import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchorType,
  IExperience,
  IExperienceTemplate,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  PAEventType,
  ProductAnalyticsEvent
} from '@dadohr/shared/domainTypes/productAnalytics';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { flatten, groupBy, orderBy, uniq } from 'lodash';
import { AlertCircle } from 'lucide-react';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import {
  ItemSorter,
  RowsRenderer,
  ROW_HEIGHTS,
  Sorters
} from '../../../../components/GroupableList';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { SortDirection, useSort } from '../../../../hooks/useSort';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  getAllActorUserIds,
  isExperienceUserActor,
  useExperienceInstanceById
} from '../../../../services/experiences/instance';
import { usePeopleTabV2Enabled } from '../../../../services/experiences/timelineItemStatus';
import { getMostRecentSuccessfulConcreteMessageOrAnyOther } from '../../../../services/experiences/message';
import { getMessageTemplateById } from '../../../../services/experiences/template';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { toMoment } from '../../../../services/time';
import { toFullName, useUserMapById } from '../../../../services/user';
import { SentItemErrorData } from '../../pages/Details/InstanceProblems';
import { SentItemErrorDetails } from '../../pages/Details/ParticipantDialog';
import { ErrorText } from '../../pages/Details/ParticipantDialogComponents/CommonStyledComponents';
import { MessageStatusChip } from '../MessageStatusChip';
import { AnchorDateDiffWarning } from '../ParticipantsTable';
import { ErrorAlert } from './Automations';
import { GroupDivider } from './components/GroupDivider';
import { NotApplicable } from './components/NotApplicable';
import { TimeZone } from './components/Timezone';
import { Participant } from './Tasks';

const SubItemContainer = styled('div')((p) => ({
  padding: p.theme.spacing(1),
  marginLeft: 9
}));

const SubTaskItem = styled('div')((p) => ({
  display: 'flex',
  gap: 8,
  margin: p.theme.spacing(1)
}));

type ColumnName =
  | 'participant'
  | 'firstDayAtWork'
  | 'scheduledDate'
  | 'sentOn'
  // | 'department' Commenting out for now since not part of ticket scope, but might be used in a later point in time
  | 'supportingActorName';

type D = {
  exp: Doc<IExperience>;
  actors: { [actorKey: string]: Doc<IUser> | null };
};

type Group = {
  key: string;
  rows: D[];
};

type OtherProps = {
  tpl: Doc<IExperienceTemplate>;
  msgId: string;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
  trackEvent: (event: ProductAnalyticsEvent) => Promise<void>;
};

const withActorOrNull = <T extends unknown>(
  d: D,
  actor: string,
  fn: (user: Doc<IUser>) => T
): T | null => {
  const a = d.actors[actor];
  return a ? fn(a) : null;
};

const withParticipantOrNull = <T extends unknown>(
  d: D,
  fn: (user: Doc<IUser>) => T
): T | null => withActorOrNull(d, PARTICIPANT_ACTOR_KEY, fn);

type Column = IColumn<D, ColumnName, OtherProps>;

const COMMON_COLUMNS: Column[] = [
  {
    key: 'firstDayAtWork',
    head: () => 'First day at work',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        if (
          o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
          o.tpl.data.anchor.field === 'hireDate'
        ) {
          const originalDate =
            u.data.hireDate && toMoment(u.data.hireDate).tz(o.tpl.data.tz);
          // This is a rare exception for broken instances which could NOT be migrated when this field was introduced
          if (!d.exp.data.anchorDate?.at) {
            return (
              <FlexContainer>
                <div>{originalDate?.format(org.dateFormats.MMDDYYYY)}</div>
              </FlexContainer>
            );
          }
          const currentDate = toMoment(d.exp.data.anchorDate.at).tz(
            o.tpl.data.tz
          );
          return (
            <FlexContainer>
              <div>{currentDate.format(org.dateFormats.MMDDYYYY)}</div>
              <AnchorDateDiffWarning
                original={originalDate}
                current={currentDate}
              />
            </FlexContainer>
          );
        }
        return u.data.hireDate
          ? toMoment(u.data.hireDate).format(org.dateFormats.MMDDYYYY)
          : '';
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },

  {
    key: 'scheduledDate',
    head: () => 'Scheduled date',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        return (
          <FlexContainer>
            {toMoment(d.exp.data.messagesStatuses[o.msgId].timing.at)
              .tz(o.tpl.data.tz)
              .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
            <TimeZone>
              {' '}
              {toMoment(d.exp.data.messagesStatuses[o.msgId].timing.at)
                .tz(o.tpl.data.tz)
                .format('z')}
            </TimeZone>
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },

  {
    key: 'sentOn',
    head: () => 'Sent on',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const { t } = useUnsafeTranslation();
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        const message = getMostRecentSuccessfulConcreteMessageOrAnyOther(
          o.msgId,
          d.exp.data.messages,
          d.exp.data.messagesStatuses
        );
        if (
          d.exp.data.messagesStatuses[o.msgId].status ===
            MessageStatus.UPCOMING ||
          !message
        ) {
          return <NotApplicable>{t('N/A')}</NotApplicable>;
        }
        //Show error sent date but in lighter gray
        if (
          !!message &&
           (message.status === MessageStatus.ERROR ||
            message.status === 'BOUNCED')
        ) {
          return (
            <NotApplicable>
              <FlexContainer>
                {toMoment(message.at)
                  .tz(o.tpl.data.tz)
                  .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}{' '}
                {toMoment(message.at).tz(o.tpl.data.tz).format('z')}
              </FlexContainer>
            </NotApplicable>
          );
        }
        return (
          <FlexContainer>
            {toMoment(message.at)
              .tz(o.tpl.data.tz)
              .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
            <TimeZone>
              {' '}
              {toMoment(message.at).tz(o.tpl.data.tz).format('z')}
            </TimeZone>
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }

  //Commenting out for now since not part of ticket scope, but might be used in a later point in time

  // {
  //   key: 'department',
  //   head: () => 'Department',
  //   cell: (d) => withParticipantOrNull(d, (u) => u.data.department),
  //   align: 'left',
  //   sortable: true,
  //   defaultDirection: 'asc',
  //   width: 150,
  //   flexGrow: 1
  // }
];

const PARTICIPANT_COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const message = getMostRecentSuccessfulConcreteMessageOrAnyOther(
          o.msgId,
          d.exp.data.messages,
          d.exp.data.messagesStatuses
        );
        return (
          <FlexContainer>
            {d.exp.data.messagesStatuses[`${o.msgId}`].status ===
            MessageStatus.UPCOMING ? (
              <MessageStatusChip
                autoWidth={true}
                status={MessageStatus.UPCOMING}
              />
            ) : d.exp.data.messagesStatuses[`${o.msgId}`].status ===
              MessageStatus.TRIGGERED ? (
              // A pending trigger-chain message has no concrete send record yet,
              // so the concrete-record branch below rendered nothing and the row
              // showed a bare name. Surface the accurate "Triggered" pill (the
              // status filter buckets it under UPCOMING so the row is not dropped).
              <MessageStatusChip autoWidth={true} status={MessageStatus.TRIGGERED} />
            ) : d.exp.data.messagesStatuses[`${o.msgId}`].status ===
              MessageStatus.SKIPPED ? (
              // SKIPPED is a first-class People-tab status (it has its own filter
              // pill). The row is surfaced by the skip-aware status fix, but the
              // status pill next to the name went missing because this ternary had
              // no SKIPPED branch — a skipped item is not UPCOMING and has no
              // concrete send record, so `message` is null and nothing rendered.
              // Read the raw instance status directly so the "Skipped" pill shows.
              <MessageStatusChip autoWidth={true} status={MessageStatus.SKIPPED} />
            ) : (
              <>
                {message && (
                  <MessageStatusChip
                    autoWidth={true}
                    status={
                      message.status === MessageStatus.ERROR ||
                      message.status === 'BOUNCED'
                        ? MessageStatus.ERROR
                        : MessageStatus.DONE
                    }
                  />
                )}
              </>
            )}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => {
                o.onShowInstanceDetails(d.exp);
                //TODO: Add source property if Kat approves and check how to make sure event isn't fired twice when page loads
                o.trackEvent({
                  eventType: PAEventType.EXPERIENCE_INSTANCE,
                  name: 'Admin Instance View Loaded',
                  'Instance ID': d.exp.id,
                  Trigger: 'Timeline Item People Tab',
                  Explanation:
                    'Admin user views the details of a single instance'
                });
              }}
            />
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_COLUMNS
];

const SUPPORTING_ACTOR_COLUMNS: Column[] = [
  {
    key: 'supportingActorName',
    head: (o) => o?.actor.label,
    cell: (d, o) => {
      return withActorOrNull(d, o.actor.key, (u) => {
        const message = getMostRecentSuccessfulConcreteMessageOrAnyOther(
          o.msgId,
          d.exp.data.messages,
          d.exp.data.messagesStatuses
        );
        return (
          <FlexContainer>
            {d.exp.data.messagesStatuses[`${o.msgId}`].status ===
            MessageStatus.UPCOMING ? (
              <MessageStatusChip
                autoWidth={true}
                status={MessageStatus.UPCOMING}
              />
            ) : d.exp.data.messagesStatuses[`${o.msgId}`].status ===
              MessageStatus.TRIGGERED ? (
              // A pending trigger-chain message has no concrete send record yet,
              // so the concrete-record branch below rendered nothing and the row
              // showed a bare name. Surface the accurate "Triggered" pill (the
              // status filter buckets it under UPCOMING so the row is not dropped).
              <MessageStatusChip autoWidth={true} status={MessageStatus.TRIGGERED} />
            ) : d.exp.data.messagesStatuses[`${o.msgId}`].status ===
              MessageStatus.SKIPPED ? (
              // SKIPPED is a first-class People-tab status (it has its own filter
              // pill). The row is surfaced by the skip-aware status fix, but the
              // status pill next to the name went missing because this ternary had
              // no SKIPPED branch — a skipped item is not UPCOMING and has no
              // concrete send record, so `message` is null and nothing rendered.
              // Read the raw instance status directly so the "Skipped" pill shows.
              <MessageStatusChip autoWidth={true} status={MessageStatus.SKIPPED} />
            ) : (
              <>
                {message && (
                  <MessageStatusChip
                    autoWidth={true}
                    status={
                      message.status === MessageStatus.ERROR ||
                      message.status === 'BOUNCED'
                        ? MessageStatus.ERROR
                        : MessageStatus.DONE
                    }
                  />
                )}
              </>
            )}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => {
                o.onShowInstanceDetails(d.exp);
                //TODO: Add source property if Kat approves and check how to make sure event isn't fired twice when page loads
                o.trackEvent({
                  eventType: PAEventType.EXPERIENCE_INSTANCE,
                  name: 'Admin Instance View Loaded',
                  'Instance ID': d.exp.id,
                  Trigger: 'Timeline Item People Tab',
                  Explanation:
                    'Admin user views the details of a single instance'
                });
              }}
            />
          </FlexContainer>
        );
      });
    },
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(
        d,
        (u) => toFullName(u.data) || u.data.email || u.data.personalEmail
      ),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_COLUMNS
];

type GroupSummary = {
  count: number;
};

const SORTERS: Sorters<GroupSummary, D, OtherProps> = {
  firstDayAtWork: {
    key: 'firstDayAtWork',
    label: 'First day at work',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d) =>
        withParticipantOrNull(d, (u) => u.data.hireDate?.toMillis()) || 0,
      dir: 'asc'
    }
  },

  scheduledDate: {
    key: 'scheduledDate',
    label: 'Scheduled date',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        d.exp.data.messagesStatuses[o.msgId]?.timing.at.toMillis() || 0,
      dir: 'desc'
    }
  },

  sentOn: {
    key: 'sentOn',
    label: 'Sent On',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        d.exp.data.messagesStatuses[o.msgId]?.timing.at.toMillis() || 0,
      dir: 'asc'
    }
  }
};

const ROW_TO_KEY = (d: D) => d.exp.id;
const DEFAULT_SORTER = SORTERS.scheduledDate;

const ExpandableErrorMessage = ({
  supportingActors,
  erroredMessage
}: {
  supportingActors: IExperienceTemplate['supportingActors'];
  erroredMessage: SentItemErrorData;
}) => {
  return (
    <>
      <SubItemContainer>
        <SubTaskItem>
          <ErrorText>
            <ErrorAlert>
              <AlertCircle size={18} />
              <SentItemErrorDetails
                sentItem={erroredMessage}
                supportingActors={supportingActors}
              />
            </ErrorAlert>
          </ErrorText>
        </SubTaskItem>
      </SubItemContainer>
    </>
  );
};

export const formatMessageToExpandableComponent = (
  rows: D[],
  msgTplId: string,
  tpl: Doc<IExperienceTemplate>
) => {
  const tplMessage = getMessageTemplateById(tpl, msgTplId);
  if (tplMessage) {
    return new Map(
      rows
        .map((row) => ({
          row,
          message: getMostRecentSuccessfulConcreteMessageOrAnyOther(
            msgTplId,
            row.exp.data.messages,
            row.exp.data.messagesStatuses
          )
        }))
        .filter(
          ({ message }) =>
            // `message` is NULL for instances where this message is SKIPPED (no
            // concrete send record). Skipped rows only reach this map since the
            // skip-aware People-tab status fix stopped dropping them upstream —
            // without the null guard ONE skipped instance crashed the whole tab
            // (TypeError: cannot read 'status' of null).
            !!message &&
            (message.status === MessageStatus.ERROR ||
              message.status === 'BOUNCED')
        )
        .map(({ row, message }) => {
          const sentErrorMessageItem: SentItemErrorData = {
            type: 'SENT_ITEM',
            tpl: tpl,
            instance: row.exp,
            id: tplMessage.id,
            humanReadableTitle: tplMessage.subject,
            includedItems: [],
            itemTplId: tplMessage.id,
            actor: tplMessage.actor,
            message: message,
            channels: tplMessage.channels
          };
          return [
            row.exp.id,
            <ExpandableErrorMessage
              supportingActors={tpl.data.supportingActors}
              erroredMessage={sentErrorMessageItem}
            />
          ];
        })
    );
  }
};

// Only ERROR/BOUNCED rows are expandable (matches formatMessageToExpandableComponent's
// filter). The pseudo-instance's faked concrete log reproduces that classification, so
// v2 keeps the same expandable set and lazy-loads the real error payload on expand.
const isErroredMessageRow = (row: D, msgTplId: string): boolean => {
  const m = getMostRecentSuccessfulConcreteMessageOrAnyOther(
    msgTplId,
    row.exp.data.messages,
    row.exp.data.messagesStatuses
  );
  return !!m && (m.status === MessageStatus.ERROR || m.status === 'BOUNCED');
};

const LazyMessageRowExpand = ({
  row,
  msgTplId,
  tpl
}: {
  row: D;
  msgTplId: string;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const [full] = useExperienceInstanceById(row.exp.id);
  if (!full) {
    return null;
  }
  const element = formatMessageToExpandableComponent(
    [{ ...row, exp: full }],
    msgTplId,
    tpl
  )?.get(full.id);
  return <>{element ?? null}</>;
};

export const MessagesExecutionReportTable = ({
  instances,
  msgId,
  msgTitle,
  tpl,
  actor,
  onShowInstanceDetails
}: {
  instances: Doc<IExperience>[];
  msgId: string;
  msgTitle: string;
  tpl: Doc<IExperienceTemplate>;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
}) => {
  const { trackEvent } = useProductAnalytics();
  const peopleTabV2Expand = usePeopleTabV2Enabled();
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, OtherProps>>(
    DEFAULT_SORTER,
    DEFAULT_SORTER.items.dir
  );

  const currentUser = useCurrentUser();
  const { org } = currentUser;

  const weNeedGrouping = sortBy.key === 'scheduledDate';

  const userIds = useMemo(
    () => uniq(flatten(instances.map((d) => getAllActorUserIds(d)))),
    [instances]
  );

  const toRows = useCallback(
    (userMap: { [key: string]: Doc<IUser> }) =>
      instances.map<D>((exp) => {
        const actors: { [actorKey: string]: Doc<IUser> } = {};
        exp.data.actors.filter(isExperienceUserActor).forEach((a) => {
          actors[a.key] = userMap[a.userId] || null;
        });
        return {
          exp,
          actors: actors
        };
      }),
    [instances]
  );

  const [rows] = useMappedLoadingValue(
    useUserMapById(tpl.data.orgId, userIds),
    toRows,
    true
  );

  const otherProps: OtherProps = useMemo(
    () => ({ tpl, msgId, actor, onShowInstanceDetails, trackEvent }),
    [tpl, msgId, actor, onShowInstanceDetails, trackEvent]
  );

  const groups = useMemo(() => {
    if (!rows) return [];
    const grouped = groupBy(rows, (r) => {
      if (r.exp.data.messagesStatuses[msgId].status === 'UPCOMING') {
        return moment(r.exp.data.messagesStatuses[msgId].timing.at.toMillis())
          .startOf('d')
          .valueOf();
      }
      const message = getMostRecentSuccessfulConcreteMessageOrAnyOther(
        msgId,
        r.exp.data.messages,
        r.exp.data.messagesStatuses
      );
      //fallback incase message isn't found
      if (!message) {
        return moment(r.exp.data.messagesStatuses[msgId].timing.at.toMillis())
          .startOf('d')
          .valueOf();
      }
      return moment(message.at.toMillis()).startOf('d').valueOf();
    });

    return orderBy(
      Object.keys(grouped).map((group) => ({
        key: group,
        rows: grouped[group]
      })),
      (s) => parseInt(s.key),
      direction
    );
  }, [rows, msgId, direction]);

  const columns =
    actor.key === PARTICIPANT_ACTOR_KEY
      ? PARTICIPANT_COLUMNS
      : SUPPORTING_ACTOR_COLUMNS;

  if (!rows) {
    return null;
  }

  // v2: rows are projection-backed pseudo-instances (no real error payload) → keep
  // the same error/bounced expandable set (via the faked concrete log) but lazy-load
  // the full instance per expanded row for the real error detail. v1: precompute.
  const rowsWithErrorMessages = peopleTabV2Expand
    ? new Map<string, JSX.Element>(
        rows
          .filter((row) => isErroredMessageRow(row, msgId))
          .map((row) => [
            row.exp.id,
            <LazyMessageRowExpand row={row} msgTplId={msgId} tpl={tpl} />
          ])
      )
    : formatMessageToExpandableComponent(rows, msgId, tpl);

  if (weNeedGrouping) {
    return (
      <GroupedMessageTable
        groups={groups}
        renderGroupDivider={(group) =>
          moment(Date.now()).isBefore(parseInt(group.key)) ? (
            <div>
              Send date:{' '}
              {moment(parseInt(group.key)).format(org.dateFormats.MMDDYYYY)}
            </div>
          ) : (
            <div>
              Sent on:{' '}
              {moment(parseInt(group.key)).format(org.dateFormats.MMDDYYYY)}
            </div>
          )
        }
        columns={columns}
        sortBy={sortBy}
        direction={direction}
        otherProps={otherProps}
        setSort={setSort}
        sidePadding
        expandableElements={rowsWithErrorMessages}
        onExpansionClick={(rowId: string) =>
          trackEvent({
            eventType: PAEventType.EXPERIENCE_TPL,
            name: 'Timeline Item Modal People Tab Item Expanded',
            Explanation:
              'Admin user expands a row in the People Tab, to see the details of the timeline item in one instance',
            'Timeline Item ID': msgId,
            'Experience Template ID': tpl.id,
            'Timeline Item Title': msgTitle,
            'Timeline Item Type': 'MESSAGE',
            'Status of Expanded Item': 'ERROR'
          })
        }
      />
    );
  }
  return (
    <RowsRenderer
      variant="contained"
      columns={columns}
      rows={rows}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
      expandableElements={rowsWithErrorMessages}
      sidePadding
      onExpansionClick={(rowId: string) =>
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Timeline Item Modal People Tab Item Expanded',
          Explanation:
            'Admin user expands a row in the People Tab, to see the details of the timeline item in one instance',
          'Timeline Item ID': msgId,
          'Experience Template ID': tpl.id,
          'Timeline Item Title': msgTitle,
          'Timeline Item Type': 'MESSAGE',
          'Status of Expanded Item': 'ERROR'
        })
      }
    />
  );
};

const GroupedMessageTable = ({
  groups,
  renderGroupDivider,
  columns,
  sortBy,
  direction,
  otherProps,
  setSort,
  sidePadding,
  expandableElements,
  onExpansionClick
}: {
  groups: Group[];
  renderGroupDivider: (group: Group) => React.ReactNode;
  columns: Column[];
  sortBy: ItemSorter<D, OtherProps>;
  direction: SortDirection;
  otherProps: OtherProps;
  setSort: (
    nextSortBy: ItemSorter<D, OtherProps>,
    nextDirection: SortDirection
  ) => void;
  sidePadding?: boolean;
  expandableElements?: Map<string, JSX.Element>;
  onExpansionClick?: (rowId: string) => Promise<void>;
}) => {
  return (
    <>
      <RowsRenderer
        variant="contained"
        columns={columns}
        rows={[]}
        renderHead={true}
        sorter={sortBy}
        sortDirection={direction}
        otherProps={otherProps}
        rowToKey={ROW_TO_KEY}
        onHeadClick={(c, dir) => {
          const sorter = SORTERS[c.key];
          if (!sorter) {
            return;
          }
          setSort(sorter, dir || sorter.items.dir);
        }}
        sidePadding={sidePadding}
      />
      {groups.map((group) => {
        if (!group.rows.length) return null;
        return (
          <div key={group.key}>
            <GroupDivider>{renderGroupDivider(group)}</GroupDivider>
            <RowsRenderer
              variant="contained"
              columns={columns}
              rows={group.rows}
              rowToKey={ROW_TO_KEY}
              sorter={sortBy}
              sortDirection={direction}
              renderHead={false}
              headProps={{ sticky: true }}
              onHeadClick={(c, dir) => {
                const sorter = SORTERS[c.key];
                if (!sorter) {
                  return;
                }
                setSort(sorter, dir || sorter.items.dir);
              }}
              chunkSize={30}
              rootMargin="300px"
              rowHeight={ROW_HEIGHTS.dense}
              otherProps={otherProps}
              expandableElements={expandableElements}
              sidePadding={sidePadding}
              onExpansionClick={onExpansionClick}
            />
          </div>
        );
      })}
    </>
  );
};
